import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert, Checkbox, FormControlLabel, FormGroup,
} from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'select-session', title: 'Select Session' },
  { id: 'mark-attendance', title: 'Mark Attendance' },
  { id: 'review', title: 'Review & Submit' },
];

interface AttendanceRecord {
  studentId: string;
  studentName: string;
  present: boolean;
  notes: string;
}

interface FormValues {
  sessionId: string;
  sessionDate: string;
  tutorId: string;
  subject: string;
  attendanceRecords: AttendanceRecord[];
  generalNotes: string;
}

const INITIAL_VALUES: FormValues = {
  sessionId: '',
  sessionDate: '',
  tutorId: '',
  subject: '',
  attendanceRecords: [],
  generalNotes: '',
};

const stepDescriptions: Record<string, string> = {
  'select-session': "Choose the session and mark attendance for each enrolled student.",
  'mark-attendance': "Mark each student's attendance status and add any notes.",
  review: "Review attendance records before submitting.",
};

export const AttendanceWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const entityResult = await dataProvider.create('tutoring-attendance', {
      data: {
        sessionId: v.sessionId,
        sessionDate: v.sessionDate,
        tutorId: v.tutorId,
        subject: v.subject,
        attendanceRecords: v.attendanceRecords,
        generalNotes: v.generalNotes,
        status: 'Submitted',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('attendance', 'draft', ['SUBMIT']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'attendance',
        entityId,
        entityName: `Attendance ${v.sessionDate}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const updateAttendance = (index: number, field: keyof AttendanceRecord, value: unknown) => {
    const records = [...(vals.attendanceRecords || [])];
    records[index] = { ...records[index], [field]: value };
    updateField('attendanceRecords', records);
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'attendance:create', context: vals as unknown as Record<string, unknown> });

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'select-session':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Session ID" value={vals.sessionId} onChange={e => updateField('sessionId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Session Date" type="date" value={vals.sessionDate} onChange={e => updateField('sessionDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Tutor ID" value={vals.tutorId} onChange={e => updateField('tutorId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Subject" value={vals.subject} onChange={e => updateField('subject', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        );
      case 'mark-attendance':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Mark attendance for each student enrolled in this session.</Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              {vals.attendanceRecords.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No attendance records. Students will be loaded from the session.</Typography>
              ) : (
                vals.attendanceRecords.map((record, idx) => (
                  <Paper key={record.studentId} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2"><strong>{record.studentName}</strong></Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={record.present}
                              onChange={e => updateAttendance(idx, 'present', e.target.checked)}
                            />
                          }
                          label="Present"
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField
                          label="Notes"
                          value={record.notes}
                          onChange={e => updateAttendance(idx, 'notes', e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="General Notes" value={vals.generalNotes} onChange={e => updateField('generalNotes', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review attendance before submitting.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Session Info</Typography>
                <Typography variant="body2"><strong>Session:</strong> {vals.sessionId}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {vals.sessionDate}</Typography>
                <Typography variant="body2"><strong>Subject:</strong> {vals.subject}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Attendance Summary</Typography>
                <Typography variant="body2"><strong>Total Students:</strong> {vals.attendanceRecords.length}</Typography>
                <Typography variant="body2"><strong>Present:</strong> {vals.attendanceRecords.filter(r => r.present).length}</Typography>
                <Typography variant="body2"><strong>Absent:</strong> {vals.attendanceRecords.filter(r => !r.present).length}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Attendance Submitted"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Session', value: vals.sessionId },
            { label: 'Date', value: vals.sessionDate },
            { label: 'Present', value: `${vals.attendanceRecords.filter(r => r.present).length}/${vals.attendanceRecords.length}` },
          ]}
          footerActions={[
            { label: 'View Attendance', to: '/tutoring-attendance' },
            { label: 'Submit Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
